/**
 * `TerminalConfig` — the single object a host passes to `<Terminal config={…} />`.
 *
 * The component owns ALL the wiring: from this config it builds the
 * `@paradigm/terminal-core` device-identity store, agent-url/token store, and
 * chat-history store, and (per connection) the OpenClaw WebSocket client. Hosts
 * never touch core factories.
 *
 * Design goals:
 *  - Minimal required surface. A host supplies identity + namespace + gateway;
 *    everything else has sensible defaults.
 *  - The default `keyFor` reproduces Paradex's historical localStorage key
 *    shapes (`<namespace>_terminal_chat_history:<addr>:<sid>`, …) so existing
 *    chat history loads with zero migration when `namespace === 'paradex'`.
 *  - Host-coupled extras (generative-UI components, positions feed, subkey
 *    card, session-sync, model catalog) are OPTIONAL injections; when absent
 *    the corresponding feature is simply off.
 */
import {
  createAgentUrlStore,
  createChatHistoryStore,
  createDeviceIdentityStore,
  createLegacyDeviceKeyMigrator,
  createMemoryStorage,
  type AgentUrlStore,
  type ChatHistoryStore,
  type DeviceIdentityStore,
  type SessionFolder,
} from '@paradigm/terminal-core';
import type { ComponentType } from 'react';

/** Feature toggles for whole subsystems. All default to `false`/off. */
export interface TerminalFeatureFlags {
  /** Dispatch `ui.render` catalog/MCP specs. Needs `components` to render. */
  readonly generativeUi?: boolean;
  /** Custom MCP-app iframe tabs (`app.register`). */
  readonly mcpApps?: boolean;
  /** Cron tab. */
  readonly cron?: boolean;
}

/**
 * Optional host capability injections. Each is independent; omit to disable
 * the feature that depends on it.
 */
export interface TerminalCapabilities {
  // The model picker is fully native and takes no host injection: it fetches
  // the live catalog from the gateway's `/api/models` (`fetchGatewayModels`),
  // labels ids via `defaultModelLabel`, and defaults to Opus. The gateway is
  // the ONLY source — there is no built-in fallback list, so the picker shows a
  // placeholder until the catalog lands rather than a hardcoded guess that
  // could name models this gateway does not serve.
  /** Live positions feed for position-backed widgets. Omit ⇒ those widgets hidden. */
  readonly positionsFeed?: { usePositions(): readonly unknown[] };
  /**
   * Catalog of components the generative-UI renderer mounts by key — an
   * `AgentSpec` child `{ component: 'metric_card', props }` renders
   * `components['metric_card']` with `props`. Omit (with `features.generativeUi`
   * off) ⇒ catalog disabled. Unknown keys render a small fallback.
   */
  readonly components?: Readonly<
    Record<string, ComponentType<Record<string, unknown>>>
  >;
  /**
   * Registry for legacy `widgetType` chat messages — a message with
   * `widgetType: 'order_preview'` renders `widgets['order_preview']` with the
   * message's `widgetData`. Omit ⇒ widget messages show a placeholder.
   */
  readonly widgets?: Readonly<
    Record<string, ComponentType<{ data: Record<string, unknown> }>>
  >;
  /** Host-provided icons; the component ships defaults. */
  readonly icons?: Readonly<Record<string, ComponentType<unknown>>>;
}

export interface TerminalConfig {
  // ──────── Identity ────────
  /** Account scope for storage keys + client id. `null` ⇒ signed out (no auto-connect). */
  readonly accountId: string | null;
  /** Bearer token for session-sync / shim REST (NOT the gateway device token). */
  readonly getAccessToken?: () => Promise<string | null>;
  /** Subscribe to account changes so the component re-hydrates. Returns an unsubscribe. */
  readonly onAccountChange?: (cb: () => void) => () => void;
  /**
   * Signed-in user's display identity (sidebar account button, account menu,
   * empty-state greeting). Cloud hosts pass the IdP profile (e.g. Clerk name
   * + email + avatar). Omit ⇒ the UI falls back to deriving a label from the
   * gateway URL — only meaningful for per-user gateway hosts (manual pairing,
   * `<name>-<hash>.terminal.dime.gg`); behind the shared gateway-proxy every
   * URL is the same host, so cloud hosts should always provide this.
   */
  readonly user?: {
    /** e.g. Clerk first/full name. Falls back to `email`'s local part. */
    readonly displayName?: string;
    /** Shown in the account-menu header instead of the gateway URL. */
    readonly email?: string;
    /** Real profile picture; replaces the bundled placeholder avatars. */
    readonly avatarUrl?: string;
  };

  // ──────── Namespacing / storage ────────
  /** localStorage namespace, e.g. `'paradex' | 'paradigm' | 'standalone'`. Drives the default key shapes + device key. */
  readonly namespace: string;
  /** Override the default key shape. Defaults to Paradex's historical convention. */
  readonly keyFor?: (
    scope: string,
    accountId: string,
    ...extra: string[]
  ) => string;
  /** Optional one-time legacy-key migration (e.g. Paradigm's suffix shape). */
  readonly legacyKeyFor?: (
    scope: string,
    accountId: string,
    ...extra: string[]
  ) => string;
  /** Default folder for sessions created by this host. Defaults to `namespace`. */
  readonly defaultFolder?: string;

  // ──────── Gateway ────────
  /** Explicit gateway ws(s) URL (wins over `resolveUrl`). */
  readonly gatewayUrl?: string | null;
  /** Fallback URL resolver when no explicit/stored URL exists. */
  readonly resolveUrl?: (accountId: string) => Promise<string | null>;
  /** Pairing-approval display prefix + client-id prefix. e.g. `'paradex-dime-terminal'`. */
  readonly clientIdPrefix: string;
  /** Connection-health strategy. Default `'heartbeat'`. */
  readonly healthStrategy?: 'heartbeat' | 'circuit-breaker';
  /** Enable the pairing-token retry / pairing-role fallback path. Default `true`. */
  readonly supportsPairingRole?: boolean;
  /** Opaque catalog manifest answered on `ui.catalog.request` (only when generativeUi on). */
  readonly catalogManifest?: unknown;
  /**
   * One-time gateway token used for the handshake when local storage has none
   * yet (e.g. a standalone "paste your gateway URL + token" pairing). Seeded
   * into the token store on first successful auth, so reconnects don't need it.
   */
  readonly fallbackPairingToken?: string;
  /**
   * Mint a fresh gateway credential immediately before every ws handshake.
   * For hosts whose credential is a short-TTL bearer token (the cloud flow's
   * 5-minute DIME JWT presented to the gateway-proxy): without it, reconnects
   * replay the stale connect-time token and fail with "invalid token". A
   * non-empty result wins over the stored/fallback token; null (or a throw)
   * falls back to the stored credential. Omit for long-lived gateway tokens
   * (manual pairing, direct-gateway grants).
   */
  readonly resolveHandshakeToken?: () => Promise<string | null>;
  /**
   * Route sidecar HTTP calls (model catalog `GET /api/models`, session sync
   * `/sync/v1/*`) through a backend forward instead of the gateway host
   * derived from the ws URL. `baseUrl` is prepended verbatim (no trailing
   * slash) to the sidecar paths; `getAuthHeaders` supplies the auth headers
   * for every call — return null when no credential is currently available
   * (session sync treats that as transient and retries). Lets a cloud host
   * keep the gateway URL + credential entirely server-side. Omit ⇒ sidecar
   * calls go directly to the gateway with the stored agent token.
   */
  readonly sidecar?: {
    readonly baseUrl: string;
    readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
  };
  /**
   * When true, the gateway URL / pairing token / device token live in memory
   * only — nothing connection-related is written to `localStorage`. For hosts
   * whose connection grant is resolved server-side each session (e.g. the
   * standalone's cloud-auth flow) and must leave no local trace. Chat history
   * persistence is unaffected. Static per host — changing it at runtime does
   * not rebuild the stores.
   */
  readonly ephemeralConnection?: boolean;

  // ──────── Prompt context ────────
  //
  // Two independent framing channels — pick ONE for a given piece of framing:
  //  • `systemPreamble` — free-text, prepended to EVERY turn's payload.
  //  • `sessionContext` — one-shot key/value, sent once right after auth.
  // They do not coordinate: a host that puts the same framing in both sends it
  // twice on turn 1 (no agent-side dedup is assumed). Use `systemPreamble` for
  // durable identity/output-style that must survive long sessions; use
  // `sessionContext` for one-time operator metadata.
  /**
   * Static framing prepended to EVERY outbound send's gateway payload (not
   * shown in the chat bubble — the user still sees only what they typed). Use
   * to tell a generic agent what domain it's operating in and how to answer
   * (e.g. Paradigm's block-trade identity + terse-output directive), re-asserted
   * on every turn. Blank / whitespace-only ⇒ no-op. See also `sessionContext`.
   */
  readonly systemPreamble?: string;
  /**
   * One-shot payload sent right after auth as
   * `<session-context>k1=v1;k2=v2;…</session-context>` (e.g. the operator
   * identity / output style). Bypasses visible chat history. See also
   * `systemPreamble` (setting both double-sends framing on the first turn).
   */
  readonly sessionContext?: Record<string, string>;
  /** Dedup key so heartbeat reconnects don't re-send; defaults to `accountId`. */
  readonly sessionContextDedupKey?: string;

  // ──────── Side effects ────────
  /** Persist a freshly-paired agent URL to the host profile. */
  readonly persistAgentUrl?: (url: string) => void;
  /**
   * Host "log out / unpair" action, surfaced as the "Log Out" item in the
   * sidebar account menu. Omit ⇒ the menu hides that action.
   */
  readonly onLogout?: () => void;
  /**
   * Label for the `onLogout` action (sidebar menu + stuck-phase escape link).
   * Default "Log Out". Use when the action's real semantics differ — e.g. the
   * native-auth silent path labels its packaged unlink action
   * "Unlink account", since a tethered session has nothing to log out of.
   */
  readonly logoutLabel?: string;
  /**
   * Optional host "install / download the PWA" action, surfaced as a "Download
   * App" item in the sidebar account menu. The host owns the install hook
   * (e.g. `beforeinstallprompt` capture) and should pass this only when an
   * install is actually offered (Chromium with a deferred prompt) and the app
   * isn't already running standalone — so the menu item appears only when
   * clicking it would do something.
   */
  readonly onDownloadApp?: () => void;
  /** Pipe errors to the host's logging. */
  readonly logException?: (
    error: unknown,
    ctx?: Record<string, unknown>,
  ) => void;

  /**
   * Optional connection diagnostics hook. Receives gateway connection
   * lifecycle events (`connect`, `open`, `close` with code/reason, `error`,
   * `retries-exhausted`, and `status` transitions) so a host can log why a
   * connection stalls. Omit ⇒ silent.
   */
  readonly onConnectionDebug?: (
    event: string,
    detail?: Record<string, unknown>,
  ) => void;

  // ──────── Optional ────────
  readonly features?: TerminalFeatureFlags;
  readonly capabilities?: TerminalCapabilities;
}

// ── Default key shapes (match Paradex's historical localStorage keys) ──
// `<namespace>_<infix>:<accountId>[:<extra…>]`
const SCOPE_INFIX: Readonly<Record<string, string>> = {
  // agent-url store scopes
  agentUrl: 'ai_agent_url',
  agentToken: 'ai_agent_token',
  deviceToken: 'ai_agent_device_token',
  // chat-history store scopes
  sessions: 'terminal_sessions',
  customFolders: 'terminal_custom_folders',
  messages: 'terminal_chat_history',
  historyUi: 'terminal_history_ui',
  tombstones: 'terminal_tombstones',
  legacyHistory: 'terminal_chat_history',
  veryLegacyHistory: 'terminal_chat_history',
  pendingCleanup: 'terminal_pending_cleanup',
};

/** Build the Paradex-shaped default `keyFor` for a namespace. */
export function makeDefaultKeyFor(
  namespace: string,
): (scope: string, accountId: string, ...extra: string[]) => string {
  return (scope, accountId, ...extra) => {
    const infix = SCOPE_INFIX[scope] ?? scope;
    return [`${namespace}_${infix}`, accountId, ...extra].join(':');
  };
}

/** The core stores the component wires up from a `TerminalConfig`. */
export interface TerminalStores {
  readonly urlStore: AgentUrlStore;
  readonly historyStore: ChatHistoryStore;
  readonly deviceStore: DeviceIdentityStore;
}

/**
 * Build the device-identity, agent-url, and chat-history stores from a config.
 * Stable for a given `namespace`; the component memoizes on that key.
 */
export function buildTerminalStores(config: TerminalConfig): TerminalStores {
  const keyFor = config.keyFor ?? makeDefaultKeyFor(config.namespace);
  const deviceKey = `${config.namespace}_terminal_device`;

  // The `selectedModel` scope is gone (each session's model now lives on the
  // gateway), so nothing can address its key again — including for hosts whose
  // stated contract is to leave no local trace, where the removed
  // `modelStorage` wiring wrote it to localStorage even in ephemeral mode.
  // Sweep it rather than orphaning a dead key on every returning user.
  if (config.accountId != null) {
    // A host with its own `keyFor` still maps the retired scope; the default
    // one no longer does (the `SCOPE_INFIX` entry went with it), so fall back
    // to the literal historical shape rather than deriving a key that never
    // existed.
    const legacyModelKey =
      config.keyFor?.('selectedModel', config.accountId) ??
      `${config.namespace}_terminal_selected_model:${config.accountId}`;
    try {
      localStorage.removeItem(legacyModelKey);
    } catch {
      // Storage unavailable (private mode / SSR) — nothing to clean up.
    }
  }

  const urlStore = createAgentUrlStore({
    keyFor,
    fallbackUrl: config.gatewayUrl ?? undefined,
    legacyKeyFor: config.legacyKeyFor,
    storage:
      config.ephemeralConnection === true ? createMemoryStorage() : undefined,
  });

  const historyStore = createChatHistoryStore({
    keyFor,
    defaultFolder: (config.defaultFolder ?? config.namespace) as SessionFolder,
    legacyMigration: 'one-level',
  });

  const deviceStore = createDeviceIdentityStore({
    storageKey: deviceKey,
    idbName: deviceKey,
    legacyMigrator: createLegacyDeviceKeyMigrator({
      legacyStorageKey: deviceKey,
    }),
  });

  return { urlStore, historyStore, deviceStore };
}
